import { FC, useEffect, useMemo, useState } from 'react'
import theme from 'theme'

// Components
import {
  Wrapper,
  SectionTitle,
  ConnectionCard,
  ConnectionHeader,
  DomainInfo,
  EditButton,
  ConnectionToggle,
  StyledSwitch,
  AddButton,
  EmptyState,
  PendingBadge
} from './SSOsupport.styles'
import { Box, CircularProgress, Typography } from '@mui/material'
import { disabledStyles, ThemeButton } from '../../../../components/buttons'
import { PlusIcon } from '../../../../components/svg'
import { ConnectionI, UserRoleSummary } from '../../../../../models'
import axios from '../../../../../lib/axios'
import { useAuth } from '@frontegg/react'
import { hasAnyRole, Role } from 'lib/utils'
import { useAppDispatch } from '../../../../../store/hooks'
import { openToast, closeToast, ToastVariant } from '../../../../../store/toasts/toastSlice'
import { PermissionTooltip } from '../../../../components/tooltip'
import SSOConnectionDrawer from './SSOConnectionDrawer'

interface Props {
  roles: UserRoleSummary[]
}

const SSOsupport: FC<Props> = ({ roles }) => {
  const dispatch = useAppDispatch()
  const { user: authUser } = useAuth()
  const [loading, setLoading] = useState(true)
  const [connections, setConnections] = useState<ConnectionI[]>([])
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedConnection, setSelectedConnection] = useState<ConnectionI | null>(null)

  useEffect(() => {
    fetchConnections()
  }, [])

  const fetchConnections = async () => {
    try {
      setLoading(true)
      const resp = await axios.get('/api/v3/sso')
      const sorted = [...(resp.data.data as ConnectionI[])].sort((a, b) =>
        (b.created_at ?? '').localeCompare(a.created_at ?? '')
      )
      setConnections(sorted)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const canEdit = useMemo(() => {
    return hasAnyRole([Role.Owner, Role.Admin], authUser)
  }, [authUser])

  const handleOpenDrawer = (connection: ConnectionI | null = null) => {
    setSelectedConnection(connection)
    setDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setSelectedConnection(null)
  }

  const handleDrawerSuccess = () => {
    fetchConnections()
  }

  const handleToggleConnection = async (connection: ConnectionI) => {
    if (!canEdit) return

    try {
      await axios.put('/api/v3/sso', {
        domain: connection.domain,
        sso_url: connection.sso_url,
        public_certificate: connection.public_certificate,
        default_user_role_id: connection.default_user_role_id,
        enabled: !connection.enabled
      })
      fetchConnections()
    } catch (e: any) {
      const message = e?.response?.data?.message || 'Something went wrong'
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed to update connection',
            description: message
          }
        })
      )
      setTimeout(() => dispatch(closeToast()), 5000)
    }
  }

  return (
    <>
      {loading ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', width: '100%' }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {connections.length ? (
            <Wrapper>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                <SectionTitle>SAML SSO Connections</SectionTitle>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {connections.map((connection) => (
                    <ConnectionCard key={connection.domain}>
                      <ConnectionHeader>
                        <DomainInfo>
                          <Box className="icon-wrapper">
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 18 18"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M9 16.5C13.1421 16.5 16.5 13.1421 16.5 9C16.5 4.85786 13.1421 1.5 9 1.5C4.85786 1.5 1.5 4.85786 1.5 9C1.5 13.1421 4.85786 16.5 9 16.5Z"
                                stroke="#A88CA4"
                                strokeWidth="1.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M1.5 9H16.5M9 1.5C10.876 3.55376 11.9421 6.21903 12 9C11.9421 11.781 10.876 14.4462 9 16.5C7.12404 14.4462 6.05794 11.781 6 9C6.05794 6.21903 7.12404 3.55376 9 1.5Z"
                                stroke="#A88CA4"
                                strokeWidth="1.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </Box>
                          <Box className="info-content">
                            <Typography className="info-label">Domain name</Typography>
                            <Typography className="info-value">{connection.domain}</Typography>
                          </Box>
                        </DomainInfo>

                        <EditButton onClick={() => handleOpenDrawer(connection)} disabled={!canEdit}>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6667 1.44775C12.9143 1.44775 13.1595 1.49653 13.3883 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.6667L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z"
                              stroke="#FFA9FC"
                              strokeWidth="1.2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <Typography className="button-text">Edit</Typography>
                        </EditButton>
                      </ConnectionHeader>

                      {connection.verified ? (
                        <ConnectionToggle>
                          <Typography className="toggle-label">Enable connection</Typography>
                          <StyledSwitch
                            checked={connection.enabled}
                            onChange={() => handleToggleConnection(connection)}
                            disabled={!canEdit}
                          />
                        </ConnectionToggle>
                      ) : (
                        <PendingBadge>Pending Approval</PendingBadge>
                      )}
                    </ConnectionCard>
                  ))}
                </Box>
              </Box>

              <AddButton onClick={() => handleOpenDrawer(null)} disabled={!canEdit}>
                <Typography className="button-text">Add New Connection</Typography>
              </AddButton>
            </Wrapper>
          ) : (
            <EmptyState>
              <Typography className="empty-title">No SSO Connections</Typography>
              <Typography className="empty-desc">There are currently no SSO Connections configured. </Typography>
              {canEdit ? (
                <ThemeButton themevariant="primary" type="button" onClick={() => handleOpenDrawer(null)}>
                  <>
                    <PlusIcon />
                    Add New Connection
                  </>
                </ThemeButton>
              ) : (
                <PermissionTooltip
                  PopperProps={{
                    sx: {
                      marginTop: `-${theme.spacing.sm}px !important`
                    }
                  }}
                  title={
                    <>
                      <Box className="tooltip-wrapper-permissions">
                        <Typography className="tooltip-text">Insufficient permissions</Typography>
                      </Box>
                    </>
                  }
                >
                  <ThemeButton themevariant="primary" type="button" sx={disabledStyles.secondary}>
                    <>
                      <PlusIcon />
                      Add New Connection
                    </>
                  </ThemeButton>
                </PermissionTooltip>
              )}

              <svg
                className="empty-img"
                xmlns="http://www.w3.org/2000/svg"
                width="504"
                height="511"
                viewBox="0 0 504 511"
                fill="none"
              >
                <path
                  d="M518.784 497.221C539.28 458.934 551 415.424 551 369.502C551 366.93 550.947 364.358 550.894 361.786C550.867 360.938 550.814 360.116 550.788 359.267C550.708 357.544 550.655 355.847 550.549 354.123C550.496 353.142 550.417 352.161 550.337 351.154C550.258 349.616 550.125 348.052 550.019 346.514C549.913 345.48 549.807 344.446 549.701 343.385C549.568 341.9 549.409 340.416 549.25 338.957C549.117 337.897 548.985 336.836 548.852 335.776C548.667 334.317 548.481 332.859 548.269 331.427C548.136 330.367 547.977 329.333 547.792 328.272C547.58 326.814 547.341 325.382 547.102 323.924C546.917 322.916 546.731 321.882 546.545 320.875C546.28 319.39 545.989 317.932 545.697 316.473C545.511 315.492 545.299 314.485 545.087 313.504C544.769 312.019 544.424 310.508 544.079 308.996C543.867 308.095 543.655 307.167 543.443 306.265C543.045 304.648 542.621 303.031 542.223 301.44C542.011 300.644 541.799 299.849 541.587 299.08C541.083 297.171 540.526 295.288 539.97 293.406C539.837 292.929 539.704 292.425 539.572 291.974C538.113 287.175 536.549 282.456 534.852 277.763C534.666 277.259 534.481 276.755 534.269 276.251C533.606 274.448 532.943 272.672 532.227 270.869C531.962 270.18 531.67 269.49 531.405 268.801C530.769 267.21 530.106 265.619 529.443 264.055C529.125 263.286 528.78 262.543 528.462 261.801C527.799 260.29 527.136 258.805 526.473 257.32C526.102 256.551 525.731 255.756 525.386 254.987C524.697 253.529 524.007 252.097 523.291 250.639C522.92 249.87 522.522 249.101 522.151 248.332C521.435 246.874 520.693 245.468 519.95 244.037C519.553 243.294 519.155 242.552 518.757 241.809C517.988 240.351 517.193 238.919 516.371 237.488C516 236.798 515.602 236.109 515.204 235.393C514.356 233.908 513.481 232.423 512.606 230.939C512.234 230.355 511.89 229.719 511.518 229.109C510.537 227.492 509.53 225.901 508.522 224.31C508.23 223.859 507.939 223.382 507.647 222.905C506.374 220.943 505.075 218.981 503.776 217.045C503.723 216.966 503.67 216.913 503.643 216.833C500.833 212.75 497.942 208.72 494.946 204.796C494.654 204.398 494.336 204 494.045 203.602C492.878 202.091 491.685 200.58 490.492 199.068C490.014 198.485 489.537 197.928 489.086 197.345C488.026 196.046 486.965 194.747 485.878 193.474C485.321 192.838 484.764 192.175 484.207 191.538C483.2 190.372 482.166 189.179 481.132 188.012C480.522 187.323 479.885 186.633 479.275 185.97C478.268 184.83 477.26 183.743 476.253 182.683C475.59 181.967 474.927 181.251 474.238 180.562C473.256 179.501 472.249 178.493 471.241 177.459C470.525 176.744 469.809 176.028 469.12 175.312C468.112 174.331 467.105 173.35 466.097 172.369C465.355 171.653 464.612 170.937 463.843 170.221C462.862 169.267 461.855 168.365 460.847 167.437C460.052 166.721 459.283 165.979 458.487 165.289C457.506 164.414 456.499 163.539 455.491 162.665C454.669 161.922 453.847 161.206 452.999 160.49C452.044 159.668 451.089 158.9 450.135 158.104C449.313 157.415 448.491 156.725 447.642 156.036L447.669 156.062C395.725 114.011 328.826 90.1747 258.003 94.5761C259.064 95.5041 304.114 133.234 347.096 143.972C386.816 153.915 398.748 165.82 398.748 165.82C398.748 165.82 436.506 197.61 390.793 231.389C390.793 231.389 377.164 240.775 378.994 259.547C379.1 260.29 379.259 261.006 379.391 261.748L379.365 261.695C379.418 261.987 379.471 262.278 379.551 262.57C379.657 263.074 379.736 263.551 379.842 264.055C380.001 264.744 380.16 265.46 380.32 266.176C380.452 266.653 380.585 267.157 380.691 267.661C380.876 268.377 381.088 269.092 381.274 269.782C381.407 270.286 381.539 270.789 381.698 271.293C381.91 272.009 382.123 272.725 382.335 273.441C382.494 273.945 382.653 274.448 382.812 274.952C383.051 275.668 383.263 276.384 383.528 277.1C383.687 277.604 383.846 278.107 384.032 278.585C384.27 279.327 384.535 280.043 384.801 280.759C384.96 281.262 385.145 281.74 385.331 282.217C385.596 282.959 385.861 283.675 386.126 284.391C386.312 284.868 386.498 285.346 386.657 285.823C386.948 286.592 387.24 287.308 387.532 288.05C387.717 288.501 387.876 288.925 388.062 289.376C388.354 290.145 388.672 290.887 388.99 291.656C389.149 292.054 389.308 292.478 389.467 292.876C389.812 293.698 390.13 294.493 390.475 295.288C390.608 295.633 390.767 295.978 390.899 296.349C391.27 297.277 391.668 298.179 392.039 299.08C392.278 299.637 392.49 300.194 392.729 300.724C393.02 301.44 393.339 302.182 393.604 302.872C393.842 303.428 394.081 303.985 394.293 304.542C394.505 305.046 394.717 305.549 394.93 306.053C395.168 306.663 395.407 307.246 395.645 307.83C395.805 308.254 395.964 308.678 396.149 309.076C396.414 309.792 396.68 310.455 396.945 311.117C397.024 311.409 397.13 311.674 397.236 311.939C397.608 312.894 397.926 313.795 398.244 314.697C409.381 346.54 412.483 378.967 403.494 409.406C403.096 410.784 402.672 412.19 402.195 413.542C400.816 417.678 399.225 421.761 397.396 425.791C393.71 433.878 389.07 441.779 383.395 449.442C382.414 450.768 381.407 452.093 380.373 453.393C378.331 455.938 376.183 458.43 374.009 460.79C365.232 470.229 355.448 478.024 344.948 484.229C338.372 488.1 331.557 491.334 324.531 493.959C320.315 495.55 316.046 496.876 311.724 498.016C301.648 500.694 291.28 502.152 280.913 502.444C279.428 502.497 277.943 502.523 276.458 502.523C257.208 502.497 237.984 498.493 220.298 490.804C218.946 490.194 217.594 489.584 216.268 488.948C213.563 487.675 210.938 486.323 208.34 484.865C207.041 484.149 205.741 483.407 204.469 482.638C201.923 481.1 199.431 479.483 196.991 477.759C195.745 476.937 194.552 476.036 193.332 475.161C186.146 469.778 179.464 463.627 173.445 456.733C172.438 455.593 171.457 454.453 170.502 453.26C168.566 450.874 166.71 448.408 164.934 445.889C164.032 444.616 163.157 443.317 162.309 442.018C159.763 438.067 157.43 433.984 155.256 429.662C154.566 428.231 153.85 426.799 153.187 425.341C151.172 420.913 149.369 416.326 147.805 411.58C146.346 407.072 145.1 402.512 144.146 397.898C142.846 391.8 141.971 385.622 141.547 379.418C141.335 376.316 141.229 373.214 141.229 370.112C141.229 368.574 141.255 367.009 141.335 365.472C141.494 360.832 141.892 356.218 142.502 351.631C143.987 340.919 146.718 330.393 150.668 320.318C151.225 318.886 151.809 317.454 152.419 316.023C155.494 308.943 159.153 302.076 163.422 295.58C165.119 292.982 166.896 290.41 168.805 287.944C170.688 285.452 172.65 283.012 174.745 280.679C176.786 278.319 178.961 276.013 181.214 273.812C185.51 269.57 190.071 265.619 194.896 262.013C203.302 255.703 212.45 250.373 222.075 246.184C223.454 245.601 224.859 245.018 226.238 244.461C229.048 243.374 231.886 242.34 234.749 241.438C237.613 240.537 240.53 239.715 243.446 239.025C248.166 237.859 251.958 236.931 255.007 236.745C256.227 236.692 257.314 236.745 258.268 236.931C259.25 237.116 260.125 237.461 260.867 237.991C263.121 239.582 264.42 242.79 264.977 248.491C265.056 249.419 265.136 250.453 265.189 251.54C265.507 258.142 263.651 265.301 261.265 271.426C264.261 268.933 267.469 266.759 270.81 264.903C281.947 258.646 294.648 255.65 307.428 254.642C311.273 254.351 315.118 254.218 318.936 254.245C328.8 254.351 341.395 259.415 350.834 258.275C351.471 258.195 352.107 258.116 352.69 258.01C352.902 257.983 353.115 257.93 353.327 257.903C353.671 257.824 354.043 257.771 354.361 257.691C354.626 257.612 354.865 257.532 355.13 257.479C355.368 257.4 355.66 257.347 355.899 257.294C356.164 257.188 356.402 257.108 356.668 257.029C356.853 256.949 357.092 256.896 357.251 256.79C357.543 256.71 357.781 256.604 358.02 256.498C358.179 256.419 358.365 256.366 358.524 256.286C358.762 256.154 359.001 256.021 359.213 255.888C359.346 255.809 359.478 255.756 359.611 255.703C359.85 255.544 360.062 255.411 360.274 255.252C360.38 255.173 360.512 255.093 360.618 255.04C360.831 254.881 361.016 254.722 361.202 254.563C361.308 254.483 361.414 254.404 361.493 254.351C361.706 254.138 361.865 253.953 362.05 253.794C362.13 253.714 362.209 253.635 362.289 253.555C362.448 253.37 362.634 253.157 362.793 252.972C362.846 252.892 362.899 252.813 362.978 252.76C363.137 252.521 363.297 252.309 363.429 252.097C363.482 252.017 363.535 251.964 363.562 251.885C363.721 251.646 363.88 251.408 364.012 251.142C364.039 251.089 364.065 251.036 364.119 250.983C364.251 250.718 364.384 250.453 364.49 250.161C364.516 250.108 364.543 250.055 364.569 250.002C364.702 249.711 364.808 249.419 364.94 249.127C364.967 249.074 364.994 249.021 364.994 249.021C365.1 248.677 365.232 248.358 365.338 248.014V247.961C367.353 241.783 367.513 233.563 373.532 224.893C380.054 215.507 389.6 207.924 390.873 198.008C391.43 193.792 390.475 189.179 387.161 183.823C375.732 165.263 339.194 162.85 320.288 154.949C308.25 149.885 296.477 144.105 285.208 137.476C284.572 137.105 283.936 136.76 283.299 136.362C281.947 135.567 280.621 134.719 279.295 133.897C278.288 133.26 277.28 132.65 276.273 132.014C275.954 131.802 275.663 131.59 275.345 131.378C273.329 130.079 271.341 128.753 269.379 127.401C267.125 125.836 264.871 124.219 262.67 122.549C236.791 103.167 211.893 76.8381 202.109 45.3923C201.26 53.5321 200.438 61.3273 200.279 68.6982C200.253 69.9179 200.253 71.1376 200.279 72.3572C200.385 83.228 202.215 93.2239 207.863 102.318C193.73 101.974 180.79 96.7503 169.097 88.5574C163.9 84.9514 158.915 80.7357 154.222 76.1222C151.862 73.789 149.581 71.3762 147.354 68.8839C128.395 47.6195 113.971 20.3894 104.399 0C104.293 2.75748 104.24 5.54147 104.24 8.35197C104.187 22.378 105.353 36.7752 107.899 50.9868C108.403 53.7973 108.96 56.6608 109.569 59.4713C115.138 84.8189 125.32 109.132 141.123 128.885C108.668 147.048 80.5084 171.653 58.0762 200.58C52.9057 207.288 48.0269 214.182 43.4927 221.314C40.4434 226.033 37.5532 230.886 34.8221 235.791C32.0645 240.722 29.4925 245.733 27.0266 250.798C24.5871 255.888 22.3068 261.032 20.1856 266.255C18.0378 271.479 16.0756 276.755 14.2726 282.111C12.443 287.467 10.8256 292.876 9.3407 298.338C8.59826 301.069 7.90886 303.826 7.24597 306.557C3.98456 320.371 1.78377 334.45 0.723152 348.741C0.192841 355.9 -0.0458022 363.032 0.00723227 370.112C0.139808 393.736 3.3747 416.83 9.36722 438.942C9.95055 441.17 10.5869 443.37 11.2498 445.571C43.8374 555.181 143.721 639.628 263.625 644.745C268.45 644.958 273.276 645.037 278.049 644.984C287.886 644.878 297.644 644.215 307.243 643.049C311.3 642.545 315.356 641.961 319.413 641.299C322.012 640.848 324.637 640.397 327.209 639.893C329.516 639.416 331.796 638.939 334.05 638.435C338.107 637.534 342.137 636.5 346.114 635.412C353.274 633.477 360.327 631.223 367.274 628.704C373.77 626.345 380.134 623.773 386.392 620.936C389.52 619.53 392.596 618.046 395.672 616.508C400.047 614.334 404.369 612.053 408.638 609.641C411.343 608.129 414.021 606.591 416.672 604.974C421.71 601.898 426.642 598.664 431.468 595.297C434.093 593.467 436.718 591.584 439.29 589.649C446.555 584.214 453.555 578.407 460.29 572.282C463.631 569.207 466.919 566.078 470.128 562.843C482.961 549.957 494.548 535.825 504.704 520.712C507.223 516.921 509.662 513.076 512.022 509.152C514.356 505.228 516.636 501.277 518.784 497.221Z"
                  fill={theme.colors.strokePrimary}
                />
              </svg>
            </EmptyState>
          )}
        </>
      )}

      <SSOConnectionDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        connection={selectedConnection}
        roles={roles}
        onSuccess={handleDrawerSuccess}
      />
    </>
  )
}

export default SSOsupport
